import React from 'react';
import { CheckboxCell } from 'components/MeasurementDates/components/checkbox-cell/CheckboxCell';
import { TooltipContent } from 'components/MeasurementDates/components/tooltip-content/TooltipContent';
import TooltipValueViewer from 'components/ScalarSpreadsheet/utilities/tooltip-value-viewer/TooltipValueViewer';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  FUND_INVESTMENTS_COMPANIES_TITLE,
  FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE,
} from 'pages/FundInvestments/common/constants';
import { CompanyRollover } from 'services/hooks/useRolloverCompanies/rollover.types';
import { ATTRIBUTE_KEYS, cell } from './parser.constants';
import { CompaniesParserProps } from './parser.props';
import { RolloverAttribute } from './parser.types';
import { DatePicker } from '../../components/date-picker/DatePicker';
import { MDSelect } from '../../components/md-select/MDSelect';
import SimpleTooltipContent from '../../components/simple-tooltip-content/SimpleTooltipContent';
import { NONE } from '../../constants';

const companiesParser = ({
  areFundInvestmentsEnabled,
  dateOptions,
  defaultState,
  latestPossibleMarketDate,
  measurementDateOptionsByCompanyId,
  rolloverDetailCompanies,
  updateCompanyRolloverCustomDate,
  updateCompanyRolloverData,
  updateCompanyRollovers,
  updateDefaultMarketDate,
}: CompaniesParserProps) => {
  const createColumn = (value: string, defaultText?: string, customText?: string) => {
    if (defaultText && customText) {
      return {
        value,
        tooltip: <TooltipContent title={value} defaultText={defaultText} customText={customText} />,
      };
    }
    if (defaultText && !customText) {
      return {
        value,
        tooltip: <SimpleTooltipContent title={value} defaultText={defaultText} />,
      };
    }
    return {
      value,
    };
  };

  const columns: { value: string; tooltip?: React.JSX.Element }[] = [
    {
      value: areFundInvestmentsEnabled
        ? FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE
        : FUND_INVESTMENTS_COMPANIES_TITLE,
    },
    createColumn(
      'Source Date',
      'The measurement date from which data will be copied. If \'None\' is selected, the new measurement date will have no data'
    ),
    createColumn('New Market Date'),
    createColumn(
      'Keep Market Data',
      'New measurement date will update the public market data to align with the specified date',
      'New measurement date will keep the existing public market data'
    ),
    createColumn(
      'Exclude Valuations',
      'New measurement date will include the valuation, related cap tables, financials, notes, and referenced documents',
      'Exclude the valuation, so a new valuation can be started from scratch'
    ),
    createColumn(
      'Reset LTM/NTM',
      'Keeps the existing periods and LTM and NTM financials',
      'Reset the LTM and NTM financial periods to the new measurement date. The financials will have empty LTM and NTM data'
    ),
    createColumn('Exclude Documents', 'Copy all documents', 'No documents or document references will be copied'),
  ];

  const header = () =>
    columns.map(column => ({
      ...cell,
      value: column.value,
      valueViewer: column.tooltip ? (props: { cell: ICell }) => <TooltipValueViewer {...props} /> : undefined,
      className: 'table-header formatter-cell division-bottom-only',
      defaultTooltip: column.tooltip,
    }));

  const generateCheckboxCells = () =>
    ATTRIBUTE_KEYS.map(key => {
      const isDisabled = defaultState.customDate === NONE;
      return {
        readOnly: isDisabled,
        ...CheckboxCell({
          checked: defaultState[key],
          handleChange: e => updateCompanyRollovers(key, e.target.checked),
          disabled: isDisabled,
        }),
      };
    });

  const dropdownCell = MDSelect({
    options: dateOptions,
    id: 0,
    value: defaultState.customDate,
    handleChange: change => updateCompanyRolloverCustomDate(change),
  });

  const marketDateCell =
    defaultState.keepMarketData || defaultState.customDate === NONE
      ? { readOnly: true, value: '' }
      : DatePicker({
        dateValue: defaultState.marketDate,
        handleChange: updateDefaultMarketDate,
        latestPossibleMarketDate,
      });

  const checkboxCells = generateCheckboxCells();

  const rows =
    rolloverDetailCompanies.length === 1
      ? []
      : [
        [
          {
            ...cell,
            value: 'Select Default',
          },
          dropdownCell,
          marketDateCell,
          ...checkboxCells,
        ],
      ];

  const generateCompanyDropdownCell = (company: CompanyRollover) => ({
    ...MDSelect({
      options: measurementDateOptionsByCompanyId[company.companyId] ?? [],
      value: company.customDate,
      warning: company.warning,
      id: company.companyId,
      handleChange: change => updateCompanyRolloverData(company, 'customDate', change),
    }),
  });

  const generateCompanyMarketDateCell = (company: CompanyRollover) => {
    if (company.customDate === NONE || company.keepMarketData) {
      return {
        value: '',
        readOnly: true,
      };
    }

    return {
      ...DatePicker({
        dateValue: company.marketDate,
        handleChange: change => updateCompanyRolloverData(company, 'marketDate', change),
        latestPossibleMarketDate,
      }),
    };
  };

  const generateCompanyCheckboxCells = (
    keys: RolloverAttribute[],
    company: CompanyRollover,
    updateCompanyRolloverData: (company: CompanyRollover, attribute: RolloverAttribute, value: boolean) => void
  ) => {
    const { customDate, isFundInvestment } = company;

    return keys.map(key => {
      let checked = company[key] as boolean;
      let disabled = customDate === NONE;

      // Disable 'Reset Financials' for fund investments
      if (key === 'resetFinancials' && isFundInvestment) {
        checked = false;
        disabled = true;
      }

      return {
        readOnly: disabled,
        ...CheckboxCell({
          checked,
          disabled,
          handleChange: e => updateCompanyRolloverData(company, key, e.target.checked),
        }),
      };
    });
  };

  rolloverDetailCompanies.forEach(company => {
    const companyCheckboxCells = generateCompanyCheckboxCells(ATTRIBUTE_KEYS, company, updateCompanyRolloverData);
    rows.push([
      {
        ...cell,
        value: company.companyName,
      },
      generateCompanyDropdownCell(company),
      generateCompanyMarketDateCell(company),
      ...companyCheckboxCells,
    ]);
  });

  return [header(), ...rows] as Array<Array<ICell>>;
};
export default companiesParser;
